import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { X, LayoutDashboard, FolderOpen, Package, Layers, FileText, ChevronDown } from 'lucide-react';
import { clsx } from 'clsx';

const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();

  const isPagesRoute = useMemo(() => location.pathname.startsWith('/pages/'), [location.pathname]);
  const [pagesOpen, setPagesOpen] = useState(isPagesRoute);

  // Auto-expand Pages when user navigates to a /pages/* route
  useEffect(() => {
    if (isPagesRoute) setPagesOpen(true);
  }, [isPagesRoute]);

  const sections = [
    {
      heading: null,
      items: [
        {
          name: 'Dashboard',
          href: '/dashboard',
          icon: LayoutDashboard,
        },
        {
          name: 'Categories',
          href: '/categories',
          icon: FolderOpen,
        },
        {
          name: 'Products',
          href: '/products',
          icon: Package,
        },
      ],
    },
    {
      heading: 'Content Management',
      items: [
        {
          name: 'Global Sections',
          href: '/global-sections',
          icon: Layers,
        },
        {
          key: 'pages',
          name: 'Pages',
          icon: FileText,
          isOpen: pagesOpen,
          onToggle: () => setPagesOpen((v) => !v),
          children: [
            { name: 'Home', href: '/pages/home' },
            { name: 'About', href: '/pages/about' },
            { name: 'Products', href: '/pages/products' },
            { name: 'Contact', href: '/pages/contact' },
            { name: 'RFQ', href: '/pages/rfq' },
          ],
        },
      ],
    },
  ];

  const isActiveLink = (href) => location.pathname === href;
  const isActiveChild = (children) => children?.some((c) => isActiveLink(c.href));

  return (
    <>
      {/* Desktop Sidebar */}
      <div className="sidebar-desktop bg-white shadow-lg border-r border-gray-200">
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-center h-16 px-6 bg-brand-500">
            <h1 className="text-lg font-bold text-white">A2Z Admin</h1>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 overflow-y-auto">
            <div className="space-y-6">
              {sections.map((group, idx) => (
                <div key={group.heading || idx}>
                  {group.heading && (
                    <div className="px-3 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {group.heading}
                    </div>
                  )}
                  <ul className="space-y-2">
                    {group.items.map((item) => {
                      const hasChildren = Array.isArray(item.children) && item.children.length > 0;
                      if (!hasChildren) {
                        return (
                          <li key={item.name}>
                            <NavLink
                              to={item.href}
                              className={clsx(
                                'nav-link',
                                isActiveLink(item.href) && 'active'
                              )}
                            >
                              <item.icon className="h-5 w-5 mr-3" />
                              {item.name}
                            </NavLink>
                          </li>
                        );
                      }

                      const expanded = !!item.isOpen;
                      const active = isActiveChild(item.children) || isPagesRoute;

                      return (
                        <li key={item.key || item.name}>
                          <button
                            type="button"
                            onClick={item.onToggle}
                            className={clsx('nav-link w-full justify-between', active && 'active')}
                            aria-expanded={expanded}
                          >
                            <span className="flex items-center">
                              <item.icon className="h-5 w-5 mr-3" />
                              {item.name}
                            </span>
                            <ChevronDown className={clsx('h-4 w-4 transition-transform', expanded && 'rotate-180')} />
                          </button>

                          {expanded && (
                            <ul className="mt-1 ml-7 space-y-1">
                              {item.children.map((child) => (
                                <li key={child.href}>
                                  <NavLink
                                    to={child.href}
                                    className={clsx(
                                      'flex items-center px-4 py-2 text-sm text-gray-700 rounded-lg hover:bg-brand-50 hover:text-brand-700 transition-colors duration-200',
                                      isActiveLink(child.href) && 'bg-brand-100 text-brand-800 font-medium'
                                    )}
                                  >
                                    {child.name}
                                  </NavLink>
                                </li>
                              ))}
                            </ul>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-gray-200">
            <div className="text-xs text-gray-500 text-center">
              A2Z Trading v1.0
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar */}
      <div
        className={clsx(
          'sidebar-mobile',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Header with close button */}
          <div className="flex items-center justify-between h-16 px-6 bg-brand-500">
            <h1 className="text-lg font-bold text-white">A2Z Admin</h1>
            <button
              onClick={onClose}
              className="p-1 text-white hover:bg-brand-600 rounded-lg transition-colors"
              aria-label="Close sidebar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 overflow-y-auto">
            <div className="space-y-6">
              {sections.map((group, idx) => (
                <div key={group.heading || idx}>
                  {group.heading && (
                    <div className="px-3 mb-2 text-xs font-semibold text-gray-200 uppercase tracking-wider">
                      {group.heading}
                    </div>
                  )}
                  <ul className="space-y-2">
                    {group.items.map((item) => {
                      const hasChildren = Array.isArray(item.children) && item.children.length > 0;
                      if (!hasChildren) {
                        return (
                          <li key={item.name}>
                            <NavLink
                              to={item.href}
                              onClick={onClose}
                              className={clsx(
                                'nav-link',
                                isActiveLink(item.href) && 'active'
                              )}
                            >
                              <item.icon className="h-5 w-5 mr-3" />
                              {item.name}
                            </NavLink>
                          </li>
                        );
                      }

                      const expanded = !!item.isOpen;
                      const active = isActiveChild(item.children) || isPagesRoute;

                      return (
                        <li key={item.key || item.name}>
                          <button
                            type="button"
                            onClick={item.onToggle}
                            className={clsx('nav-link w-full justify-between', active && 'active')}
                            aria-expanded={expanded}
                          >
                            <span className="flex items-center">
                              <item.icon className="h-5 w-5 mr-3" />
                              {item.name}
                            </span>
                            <ChevronDown className={clsx('h-4 w-4 transition-transform', expanded && 'rotate-180')} />
                          </button>

                          {expanded && (
                            <ul className="mt-1 ml-7 space-y-1">
                              {item.children.map((child) => (
                                <li key={child.href}>
                                  <NavLink
                                    to={child.href}
                                    onClick={onClose}
                                    className={clsx(
                                      'flex items-center px-4 py-2 text-sm text-gray-700 rounded-lg hover:bg-brand-50 hover:text-brand-700 transition-colors duration-200',
                                      isActiveLink(child.href) && 'bg-brand-100 text-brand-800 font-medium'
                                    )}
                                  >
                                    {child.name}
                                  </NavLink>
                                </li>
                              ))}
                            </ul>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-gray-200">
            <div className="text-xs text-gray-500 text-center">
              A2Z Trading v1.0
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;